import { useCallback, useEffect, useState } from "react";
import { resolvePublicImageUrl } from "../../../utils/resolvePublicImageUrl";

const VISIBLE_THUMBS = 8;

export default function CasaMapaAdminListingGallery({ images = [], t }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const count = images.length;
  const visible = images.slice(0, VISIBLE_THUMBS);
  const hiddenCount = Math.max(0, count - visible.length);
  const lightboxOpen = lightboxIndex != null && count > 0;
  const current = lightboxOpen ? images[Math.min(lightboxIndex, count - 1)] : null;

  const openAt = useCallback(
    (index) => {
      if (!count) return;
      setLightboxIndex((index + count) % count);
    },
    [count]
  );

  const close = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        openAt(lightboxIndex + 1);
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        openAt(lightboxIndex - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [close, lightboxIndex, lightboxOpen, openAt]);

  if (!count) {
    return (
      <section className="cm-listing-thumbs" aria-label={t("realEstate.casaMapa.gallery")}>
        <div className="cm-listing-thumbs__empty">{t("realEstate.casaMapa.photosUnavailable")}</div>
      </section>
    );
  }

  return (
    <section className="cm-listing-thumbs" aria-label={t("realEstate.casaMapa.gallery")}>
      <ul className="cm-listing-thumbs__list">
        {visible.map((image, index) => {
          const isOverflow = index === visible.length - 1 && hiddenCount > 0;
          return (
            <li key={image.id || image.url || index}>
              <button
                type="button"
                className="cm-listing-thumbs__item"
                onClick={() => openAt(index)}
                aria-label={t("realEstate.casaMapa.showPhoto", { index: index + 1 })}
              >
                <img src={resolvePublicImageUrl(image.thumbnailUrl || image.url)} alt="" />
                {isOverflow ? (
                  <span className="cm-listing-thumbs__more">+{hiddenCount}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>

      {lightboxOpen ? (
        <div
          className="cm-listing-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t("realEstate.casaMapa.gallery")}
          onClick={close}
        >
          <img
            className="cm-listing-lightbox__image"
            src={resolvePublicImageUrl(current?.url)}
            alt=""
            onClick={(event) => event.stopPropagation()}
          />
          <p className="cm-listing-lightbox__count">
            {t("realEstate.casaMapa.photoCount", {
              current: lightboxIndex + 1,
              total: count,
            })}
          </p>
          <button
            type="button"
            className="cm-listing-lightbox__close"
            onClick={close}
            aria-label={t("realEstate.casaMapa.close")}
          >
            {t("realEstate.casaMapa.close")}
          </button>
          {count > 1 ? (
            <>
              <button
                type="button"
                className="cm-listing-lightbox__nav cm-listing-lightbox__nav--prev"
                onClick={(event) => {
                  event.stopPropagation();
                  openAt(lightboxIndex - 1);
                }}
                aria-label={t("realEstate.casaMapa.previousPhoto")}
              >
                ‹
              </button>
              <button
                type="button"
                className="cm-listing-lightbox__nav cm-listing-lightbox__nav--next"
                onClick={(event) => {
                  event.stopPropagation();
                  openAt(lightboxIndex + 1);
                }}
                aria-label={t("realEstate.casaMapa.nextPhoto")}
              >
                ›
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
